import React, { useEffect, useState, useSyncExternalStore, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import AnalyticsGate from './components/AnalyticsGate';
import ScrollToTop from './components/ScrollToTop';
import LandingShell from './components/landing/LandingShell';
import { shouldHideClara } from './claraRoutes';
// Eager (not lazy) on purpose: this boundary must catch a FAILED lazy chunk
// load (bad connection / stale deploy), so it can't live in a chunk itself.
import PortalErrorBoundary from './pages/staff/ErrorBoundary';
// Tiny + eager for the same reason: the not-found page must render even when
// the address itself is the problem.
import PortalNotFound from './pages/staff/PortalNotFound';

// Marketing shell chrome is lazy so portal sessions (/staff, /teach/portal)
// never download Header/Footer/TrialSchedulerModal/ClaraChat code they can't
// render. ClaraChat's CSS moved into ClaraChat.js so it rides the same chunk.
const Header = lazy(() => import('./components/layout/Header'));
const Footer = lazy(() => import('./components/layout/Footer'));
const TrialSchedulerModal = lazy(() => import('./components/common/TrialSchedulerModal'));
const ClaraChat = lazy(() => import('./components/ClaraChat'));

const HomePage = lazy(() => import('./pages/HomePage'));
const LessonsPage = lazy(() => import('./pages/LessonsPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const TeacherDetailPage = lazy(() => import('./pages/TeacherDetailPage'));
const InstrumentsPage = lazy(() => import('./pages/InstrumentsPage'));
const InstrumentDetailPage = lazy(() => import('./pages/InstrumentDetailPage'));
const PlayPianoPage = lazy(() => import('./pages/PlayPianoPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LinksPage = lazy(() => import('./pages/LinksPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// sflcm.com/live — client-side fallback for the vercel.json /live redirect:
// phones with a cached service worker never reach the server redirect.
function LiveRedirect() {
  React.useEffect(() => {
    window.location.replace('https://www.youtube.com/channel/UCHzRm3Xpxze6cjw_GNXHC7g/live');
  }, []);
  return null;
}

const GroupClassesPage = lazy(() => import('./pages/GroupClassesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const PoliciesPage = lazy(() => import('./pages/PoliciesPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ScholarshipsPage = lazy(() => import('./pages/ScholarshipsPage'));
const RoomRentalsPage = lazy(() => import('./pages/RoomRentalsPage'));
const CancelPage = lazy(() => import('./pages/CancelPage'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const TrialBookingPage = lazy(() => import('./pages/TrialBookingPage'));
const FindUsPage = lazy(() => import('./pages/FindUsPage'));
const TeachPage = lazy(() => import('./pages/TeachPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const HelpPortalPage = lazy(() => import('./pages/HelpPortalPage'));
const ExpoPage = lazy(() => import('./pages/ExpoPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const InstrumentLandingPage = lazy(() => import('./pages/landing/InstrumentLandingPage'));
const StaffLogin = lazy(() => import('./pages/staff/StaffLogin'));
const StaffLayout = lazy(() => import('./pages/staff/StaffLayout'));
const StaffDashboard = lazy(() => import('./pages/staff/StaffDashboard'));
const IssuesList = lazy(() => import('./pages/staff/IssuesList'));
// IssueNew (the standalone "Log an issue" page) still backs the /staff/issues/new
// route, but it's no longer a nav destination — reached only from the Action
// Center's "Log an issue" affordance (Issues v2, 2026-07-05).
const IssueNew = lazy(() => import('./pages/staff/IssueNew'));
const IssueDetail = lazy(() => import('./pages/staff/IssueDetail'));
const StaffDocViewer = lazy(() => import('./pages/staff/StaffDocViewer'));
const StaffQuiz = lazy(() => import('./pages/staff/StaffQuiz'));
const StaffQuickRef = lazy(() => import('./pages/staff/StaffQuickRef'));
const StaffShift = lazy(() => import('./pages/staff/StaffShift'));
const StaffFrontDesk = lazy(() => import('./pages/staff/StaffFrontDesk'));
const StaffTimeOff = lazy(() => import('./pages/staff/StaffTimeOff'));
const StaffInvoices = lazy(() => import('./pages/staff/StaffInvoices'));
const StaffTrialFollowup = lazy(() => import('./pages/staff/StaffTrialFollowup'));
const StaffNewStudentPlacement = lazy(() => import('./pages/staff/StaffNewStudentPlacement'));
const StaffWeeklyTeaching = lazy(() => import('./pages/staff/StaffWeeklyTeaching'));
// StaffTasks + StaffApprovals are no longer routed directly — the Action Center
// composes them (embedded) and the old /staff/tasks + /staff/approvals routes
// redirect here. They're imported inside StaffActions, so no lazy handle here.
const StaffActions = lazy(() => import('./pages/staff/StaffActions'));
// Owner-only Decide list (Design Wave 2): gathers the owner's decisions and
// deep-links each one to its existing surface; it never decides itself.
const StaffDecide = lazy(() => import('./pages/staff/StaffDecide'));
const StaffMessages = lazy(() => import('./pages/staff/StaffMessages'));
const StaffChat = lazy(() => import('./pages/staff/StaffChat'));
const StaffCancellations = lazy(() => import('./pages/staff/StaffCancellations'));
const StaffCapacity = lazy(() => import('./pages/staff/StaffCapacity'));
const StaffAtRisk = lazy(() => import('./pages/staff/StaffAtRisk'));
const StaffCompliance = lazy(() => import('./pages/staff/StaffCompliance'));
const StaffAds = lazy(() => import('./pages/staff/StaffAds'));
const StaffEconomics = lazy(() => import('./pages/staff/StaffEconomics'));
const OwnerBriefs = lazy(() => import('./pages/staff/OwnerBriefs'));
const StaffTeacherProfile = lazy(() => import('./pages/staff/StaffTeacherProfile'));
const StaffOpus1Training = lazy(() => import('./pages/staff/StaffOpus1Training'));
const StaffGuide = lazy(() => import('./pages/staff/StaffGuide'));
const StaffLearn = lazy(() => import('./pages/staff/StaffLearn'));
const LearnLibrary = lazy(() => import('./pages/staff/StaffLearn').then((m) => ({ default: m.LearnLibrary })));
const StaffPeople = lazy(() => import('./pages/staff/StaffPeople'));
const StaffApplicants = lazy(() => import('./pages/staff/StaffApplicants'));
const TeachLogin = lazy(() => import('./pages/teach/TeachLogin'));
const TeachLayout = lazy(() => import('./pages/teach/TeachLayout'));
const TeachDashboard = lazy(() => import('./pages/teach/TeachDashboard'));
const TeachSchedule = lazy(() => import('./pages/teach/TeachSchedule'));
const TeachStudents = lazy(() => import('./pages/teach/TeachStudents'));
const TeachEvents = lazy(() => import('./pages/teach/TeachEvents'));
const TeachResources = lazy(() => import('./pages/teach/TeachResources'));
const TeachOpus1 = lazy(() => import('./pages/teach/TeachOpus1'));
const TeachTimeOff = lazy(() => import('./pages/teach/TeachTimeOff'));
const TeachGuide = lazy(() => import('./pages/teach/TeachGuide'));
const TeachChat = lazy(() => import('./pages/teach/TeachChat'));
const ShopInstrumentPage = lazy(() => import('./pages/ShopInstrumentPage'));
const MockShopV2Page = lazy(() => import('./pages/MockShopV2Page'));
const PortalEmailPreviewPage = lazy(() => import('./pages/PortalEmailPreviewPage'));
const DeskBriefPreviewPage = lazy(() => import('./pages/DeskBriefPreviewPage'));
const ActionCenterPreviewPage = lazy(() => import('./pages/ActionCenterPreviewPage'));
const OwnerBriefsPreviewPage = lazy(() => import('./pages/OwnerBriefsPreviewPage'));

// ─── Route-change progress bar ───────────────────────────────────────────────
// React Router v7 wraps navigations in startTransition, so on slow networks a
// pending lazy route keeps the old page on screen with zero feedback for ~2s.
// The browser URL updates synchronously on navigate, while useLocation() only
// commits once the new route's chunk resolves — comparing the two reveals a
// pending navigation. pushState fires no event, so we patch it once to emit one.
// Purely observational: navigation behavior itself is untouched.
let historyEventsPatched = false;
function subscribeToHistory(callback) {
  if (!historyEventsPatched) {
    historyEventsPatched = true;
    ['pushState', 'replaceState'].forEach((method) => {
      const original = window.history[method].bind(window.history);
      window.history[method] = (...args) => {
        const result = original(...args);
        window.dispatchEvent(new Event('app:locationchange'));
        return result;
      };
    });
  }
  window.addEventListener('app:locationchange', callback);
  window.addEventListener('popstate', callback);
  return () => {
    window.removeEventListener('app:locationchange', callback);
    window.removeEventListener('popstate', callback);
  };
}
const getBrowserPath = () => window.location.pathname + window.location.search;

function RouteProgressBar() {
  const { pathname, search } = useLocation();
  const browserPath = useSyncExternalStore(subscribeToHistory, getBrowserPath, getBrowserPath);
  const pending = browserPath !== pathname + search;
  const [visible, setVisible] = useState(false);

  // Only surface the bar if the navigation is still pending after ~300ms —
  // fast transitions never see it.
  useEffect(() => {
    if (!pending) {
      setVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, [pending, browserPath]);

  if (!visible) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] overflow-hidden" role="progressbar" aria-label="Loading page">
      <style>{'@keyframes routeProgressSlide{0%{transform:translateX(-100%)}100%{transform:translateX(250%)}}'}</style>
      <div
        className="h-full w-2/5 bg-gradient-to-r from-primary via-secondary to-primary rounded-r-full"
        style={{ animation: 'routeProgressSlide 1.1s ease-in-out infinite' }}
      />
    </div>
  );
}

function MinimalSpinner({ tall = false }) {
  return (
    // tall: public-site pages. A full-screen placeholder keeps the lazy footer
    // below the fold until the page arrives, so it doesn't jump (CLS).
    <div className={`${tall ? 'min-h-screen' : 'min-h-[60vh]'} flex items-center justify-center`}>
      <div className="w-3 h-3 rounded-full bg-primary/60 animate-pulse" aria-label="Loading" />
    </div>
  );
}

// Instant stand-in while the lazy Header chunk loads. Mirrors the Header's two
// fixed bars (28px teal utility strip + the glass nav) so the top of a cold
// marketing paint keeps its shape — both are fixed, so content never shifts.
function HeaderFallback() {
  return (
    <div aria-hidden="true">
      <div className="fixed top-0 w-full z-[60] bg-primary h-[28px]" />
      <div className="fixed top-[28px] w-full z-50 glass-nav h-14" />
    </div>
  );
}

// Redirect a legacy route to the Action Center, PRESERVING the query string so
// deep-links keep working: /staff/approvals?action=<id> → /staff/actions?action=
// =<id>, /staff/tasks?task=<id> → /staff/actions?task=<id>. A standalone
// /staff/approvals?filter=failed carries over too (the Action Center's embedded
// Approvals also reads ?approvals=, but the plain ?filter= still lands the row).
function RedirectToActions() {
  const { search } = useLocation();
  return <Navigate to={`/staff/actions${search}`} replace />;
}

// Redirect a consolidated portal page to its new home (2026-09-28 nav
// consolidation), carrying the query string + hash so old bookmarks and deep
// links (?q=, #section) still land. `defaultHash` is the anchor to land on
// when the old link carried none (People's #team-access for /staff/team).
function RedirectKeepingQuery({ to, defaultHash = '' }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash || defaultHash}`} replace />;
}

function AppBody() {
  const { pathname } = useLocation();
  const isLanding = pathname.startsWith('/mockups/lp-') || pathname.startsWith('/lp/');
  const isStaff = pathname.startsWith('/staff');
  // Teacher portal is private + chrome-less like staff. Scoped to /teach/login
  // and /teach/portal/* so the public careers page at exact /teach (and
  // /careers) keeps the marketing Header/Footer.
  const isTeacher = pathname.startsWith('/teach/login') || pathname.startsWith('/teach/portal');
  // Hidden email-preview route: render bare (no marketing Header/Footer/nav).
  const isPreview = pathname.startsWith('/preview/');
  // Floating Clara widget is suppressed on /expo and /play-piano only
  // (see claraRoutes.js for why); every other route keeps it.
  const hideClara = shouldHideClara(pathname);

  // QR/social profile destination: focused links without the marketing chrome.
  if (/^\/links\/?$/.test(pathname)) {
    return <Suspense fallback={<MinimalSpinner />}><LinksPage /></Suspense>;
  }

  if (isLanding) {
    return (
      <LandingShell>
        <Suspense fallback={<MinimalSpinner />}>
          <Routes>
            <Route path="/lp/:instrument" element={<InstrumentLandingPage />} />
            <Route path="/mockups/lp-piano" element={<Navigate to="/lp/piano" replace />} />
          </Routes>
        </Suspense>
      </LandingShell>
    );
  }

  // Event invitations (/invite/<slug>) are standalone, phone-first pages: no
  // marketing Header/Footer/Clara — the page carries its own About footer.
  if (pathname.startsWith('/invite/')) {
    return (
      <Suspense fallback={<MinimalSpinner />}>
        <Routes>
          <Route path="/invite/:slug" element={<InvitePage />} />
          <Route path="*" element={<Navigate to="/events" replace />} />
        </Routes>
      </Suspense>
    );
  }

  if (isPreview) {
    return (
      <Suspense fallback={<MinimalSpinner />}>
        <Routes>
          <Route path="/preview/portal-emails" element={<PortalEmailPreviewPage />} />
          <Route path="/preview/desk-brief" element={<DeskBriefPreviewPage />} />
          <Route path="/preview/action-center" element={<ActionCenterPreviewPage />} />
          <Route path="/preview/owner-briefs" element={<OwnerBriefsPreviewPage />} />
        </Routes>
      </Suspense>
    );
  }

  if (isStaff) {
    return (
      // Boundary ABOVE the Suspense (review L2), same as the teacher portal: a
      // failed StaffLayout/StaffLogin chunk (stale tab after a deploy) rejects
      // at THIS Suspense, which the in-layout boundary can't catch. It now
      // auto-reloads once, then shows the branded "portal was updated" card.
      <PortalErrorBoundary resetKey={pathname}>
        <Suspense fallback={<MinimalSpinner />}>
          <Routes>
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff" element={<StaffLayout />}>
              <Route index element={<StaffDashboard />} />
              <Route path="issues" element={<IssuesList />} />
              <Route path="issues/new" element={<IssueNew />} />
              <Route path="issues/:id" element={<IssueDetail />} />
              <Route path="handbook" element={<StaffDocViewer />} />
              <Route path="training" element={<StaffDocViewer />} />
              <Route path="playbook" element={<Navigate to="/staff/handbook" replace />} />
              <Route path="open-close" element={<StaffDocViewer />} />
              {/* Empty route marker: authenticated StaffLayout owns the one lazy,
                  persistent Opus1 iframe so this child must never create another. */}
              <Route path="opus1" element={null} />
              <Route path="quiz" element={<StaffQuiz />} />
              <Route path="frontdesk" element={<StaffFrontDesk />} />
              <Route path="actions" element={<StaffActions />} />
              {/* Owner/admin-only Decide list — the page redirects everyone
                  else (and the view-as-desk preview) to Home; its read is
                  requireRole-gated server-side. */}
              <Route path="decide" element={<StaffDecide />} />
              {/* Admin-only Messages tab — the page itself gates on the effective
                  user role (view-as-frontdesk preview renders NotAvailable). */}
              <Route path="messages" element={<StaffMessages />} />
              <Route path="chat" element={<StaffChat />} />
              {/* Old routes redirect to the unified Action Center, keeping deep-link
                  query strings (?action= / ?task= / ?filter=) intact. */}
              <Route path="tasks" element={<RedirectToActions />} />
              <Route path="approvals" element={<RedirectToActions />} />
              <Route path="timeoff" element={<StaffTimeOff />} />
              <Route path="invoices" element={<StaffInvoices />} />
              <Route path="trial-followup" element={<StaffTrialFollowup />} />
              <Route path="new-student-placement" element={<StaffNewStudentPlacement />} />
              {/* Weekly teaching: owner/admin/front desk, same as the roster
                  read. The page redirects anyone else; StaffLayout also
                  bounces a role-limited path before the tab paints. */}
              <Route path="weekly-teaching" element={<StaffWeeklyTeaching />} />
              <Route path="cancellations" element={<StaffCancellations />} />
              {/* Admin-only Capacity tab — nav hides it for non-admins; the API
                  itself role-scopes the payload (staff get the picker only). */}
              <Route path="capacity" element={<StaffCapacity />} />
              {/* Mark-only At Risk (churn) view — adminOnly in staffNav hides it
                  from non-admin nav/palette; the page self-redirects non-admins
                  and the API 403s non-admin sessions (Issues gate stack). */}
              <Route path="at-risk" element={<StaffAtRisk />} />
              {/* Mark-only Compliance (policy-audit) view — same gate stack as
                  At Risk/Issues: adminOnly nav flag + page self-redirect + API
                  403 on non-admin sessions. */}
              <Route path="compliance" element={<StaffCompliance />} />
              {/* Admin-only Ads tab (daily Google + Meta check) — adminOnly nav
                  flag + page self-redirect + API 403, same stack as Compliance. */}
              <Route path="ads" element={<StaffAds />} />
              {/* OWNER-ONLY School Economics (Mark alone): ownerOnly nav flag
                  (from /auth/me owner_only) + OWNER_ONLY_PATHS bounce + page
                  self-redirect + API 403 for every other session. */}
              <Route path="economics" element={<StaffEconomics />} />
              {/* Owner/admin Owner Briefs. The page redirects everyone else,
                  then reads GET /api/staff/owner-briefs?resource=brief
                  (requireRole, read only, no DDL). Approve stays locked.
                  The fixture shell lives only at /preview/owner-briefs. */}
              <Route path="owner-briefs" element={<OwnerBriefs />} />
              {/* People — one page (2026-10-08). The old Directory and Team
                  addresses redirect to it; per-person portal account pages
                  keep /staff/directory/<opus1 id> (every People card links
                  there, and so do Home, the Action Center and old bookmarks). */}
              <Route path="people" element={<StaffPeople />} />
              <Route path="directory" element={<RedirectKeepingQuery to="/staff/people" />} />
              <Route path="directory/:key" element={<StaffTeacherProfile />} />
              <Route path="team" element={<RedirectKeepingQuery to="/staff/people" defaultHash="#team-access" />} />
              <Route path="applicants" element={<StaffApplicants />} />
              {/* Learn hub (2026-09-28): Guide, Opus1 video, Shift and Quick ref
                  are sections of Learn; their old URLs redirect in. */}
              <Route path="learn" element={<StaffLearn />}>
                <Route index element={<LearnLibrary />} />
                <Route path="guide" element={<StaffGuide embedded />} />
                <Route path="video" element={<StaffOpus1Training embedded />} />
                <Route path="shift" element={<StaffShift embedded />} />
                <Route path="quick-ref" element={<StaffQuickRef embedded />} />
                <Route path="*" element={<Navigate to="/staff/learn" replace />} />
              </Route>
              <Route path="guide" element={<RedirectKeepingQuery to="/staff/learn/guide" />} />
              <Route path="opus1-training" element={<RedirectKeepingQuery to="/staff/learn/video" />} />
              <Route path="shift" element={<RedirectKeepingQuery to="/staff/learn/shift" />} />
              <Route path="quick-ref" element={<RedirectKeepingQuery to="/staff/learn/quick-ref" />} />
              {/* Unknown /staff/… address (review L1): friendly not-found inside
                  the portal shell instead of a blank page. */}
              <Route path="*" element={<PortalNotFound />} />
            </Route>
            {/* /staffX-style addresses that miss the layout entirely. */}
            <Route path="*" element={<main className="min-h-screen bg-surface"><PortalNotFound /></main>} />
          </Routes>
        </Suspense>
      </PortalErrorBoundary>
    );
  }

  if (isTeacher) {
    return (
      // Boundary ABOVE the Suspense (red-team launch gate, 2026-07-25): the
      // in-layout PortalErrorBoundary can't catch a failed TeachLayout chunk
      // load — that rejection surfaces at THIS Suspense, and without a
      // boundary it white-screened the whole portal on a teacher's first
      // cell-data open. Now it renders the branded reload card instead.
      <PortalErrorBoundary resetKey={pathname}>
        <Suspense fallback={<MinimalSpinner />}>
          <Routes>
            <Route path="/teach/login" element={<TeachLogin />} />
            <Route path="/teach/portal" element={<TeachLayout />}>
              <Route index element={<TeachDashboard />} />
              <Route path="schedule" element={<TeachSchedule />} />
              <Route path="students" element={<TeachStudents />} />
              <Route path="students/:id" element={<TeachStudents />} />
              <Route path="timeoff" element={<TeachTimeOff />} />
              <Route path="calendar" element={<TeachEvents />} />
              <Route path="resources" element={<TeachResources />} />
              <Route path="opus1" element={<TeachOpus1 />} />
              <Route path="chat" element={<TeachChat />} />
              <Route path="guide" element={<TeachGuide />} />
              <Route path="*" element={<PortalNotFound homeTo="/teach/portal" homeLabel="Go to teacher Home" />} />
            </Route>
            <Route path="*" element={<main className="min-h-screen bg-surface"><PortalNotFound homeTo="/teach/portal" homeLabel="Go to teacher Home" /></main>} />
          </Routes>
        </Suspense>
      </PortalErrorBoundary>
    );
  }

  return (
    <div className="bg-surface text-on-surface font-body min-h-screen flex flex-col">
      <a href="#main" className="skip-link">Skip to content</a>
      {/* Header is fixed-position, so a suspended chunk causes no layout shift —
          the instant fallback paints the fixed brand strip + glass nav bar so
          the top of the page never flashes empty on a cold marketing paint. */}
      <Suspense fallback={<HeaderFallback />}>
        <Header />
      </Suspense>
      <main id="main" className="flex-1 pt-7 pb-20 lg:pb-0">
        <Suspense fallback={<MinimalSpinner tall />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/team/:slug" element={<TeacherDetailPage />} />
            <Route path="/group-classes" element={<GroupClassesPage />} />
            <Route path="/instruments" element={<InstrumentsPage />} />
            <Route path="/instruments/songwriting" element={<Navigate to="/instruments/piano" replace />} />
            <Route path="/instruments/:slug" element={<InstrumentDetailPage />} />
            <Route path="/play-piano" element={<PlayPianoPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/scholarships" element={<ScholarshipsPage />} />
            <Route path="/room-rentals" element={<RoomRentalsPage />} />
            <Route path="/cancel" element={<CancelPage />} />
            <Route path="/trial" element={<TrialBookingPage />} />
            <Route path="/quiz" element={<QuizPage />} />
            <Route path="/find-your-instrument" element={<QuizPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/find-us" element={<FindUsPage />} />
            <Route path="/shop" element={<MockShopV2Page />} />
            <Route path="/shop/:slug" element={<ShopInstrumentPage />} />
            <Route path="/expo" element={<ExpoPage />} />
            <Route path="/teach" element={<TeachPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/help/portal" element={<HelpPortalPage />} />
            <Route path="/mockups/shop-v2" element={<MockShopV2Page />} />
            <Route path="/live" element={<LiveRedirect />} />
            <Route path="/invite" element={<Navigate to="/events" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {/* Below-the-fold / floating chrome: fine to pop in when the chunk lands.
          /expo and /play-piano suppress the Clara widget (claraRoutes.js). */}
      <Suspense fallback={null}>
        <Footer />
        <TrialSchedulerModal />
        {!hideClara && <ClaraChat />}
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AnalyticsGate />
      <ScrollToTop />
      <RouteProgressBar />
      <AppBody />
    </Router>
  );
}

export default App;
